import { AgentThreadVoStatus, api, type AgentThreadVo } from "@/api";
import { useToast } from "@/component/Toast/useToast";
import { threadStore } from "@/store/thread";
import { FileAttachment } from "@/types/attachment";
import { type ChatDraft } from "@/types/draft-strorage";
import {
  attachmentsToPersisted,
  persistedToAttachments,
} from "@/util/draftConverter";
import {
  deleteDraft,
  getDraft,
  getDraftKey,
  setDraft,
} from "@/util/draftStorage";
import { uploadAttachments } from "@/util/uploadAttachments";
import { subscribeToWebPush } from "@/util/webPush";
import { TextAreaRef } from "antd/es/input/TextArea";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { VidMuseEvent } from "../common/events";
import { logger } from "../common/logger";
import { useFileUpload } from "./useFileUpload";

const stopTooltipText = "Stop creating";
const stoppingTooltipText = "Stopping...";

export function useSendMessage(
  thread?: AgentThreadVo,
  chatBoxRef?: React.RefObject<HTMLDivElement | null>
) {
  const [text, setText] = useState("");
  const [isAuto, setIsAuto] = useState<boolean>(thread?.autoMode ?? true);
  const [isAutoLoading, setIsAutoLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isRequestStopping, setIsRequestStopping] = useState(false);
  const textAreaRef = useRef<TextAreaRef>(null);
  const router = useRouter();
  const { error: showErrorToast } = useToast();
  const threadId = thread?.id;

  // Toast 显示逻辑
  const showSendMessageToast = useCallback(
    (content: string) => {
      if (!content) return;

      const options = {
        key: "input-upload-error",
      } as {
        key: string;
        style?: { marginTop: string; transform: string };
      };

      // 如果在首页，Toast 需要弹到临近输入框的位置
      const isHomePage = !threadId;
      if (isHomePage && chatBoxRef?.current) {
        const { y } = chatBoxRef.current.getBoundingClientRect();
        const topPos = Math.floor(y - 30);

        if (topPos > 0) {
          options.style = {
            marginTop: `${topPos}px`,
            transform: `translateY(-100%)`,
          };
        }
      }

      showErrorToast(content, options);
    },
    [showErrorToast, threadId, chatBoxRef]
  );

  // 使用通用文件上传 hook
  const {
    attachmentList,
    setAttachmentList,
    uploadProps,
    onRemove,
    clearAttachments,
  } = useFileUpload({
    accept: "image/*,video/*,audio/*",
    onError: (errors) => {
      if (errors.tooMany) {
        showSendMessageToast("You can upload up to 5 files per project.");
      }
      if (errors.oversized) {
        showSendMessageToast(
          "Maximum total upload size is 100MB. Try compressing them before uploading."
        );
      }
    },
  });

  // 为不同会话/页面创建唯一草稿键
  const draftKey = useMemo(() => getDraftKey(threadId), [threadId]);

  // 恢复草稿
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const draft = await getDraft(draftKey);
        if (!isMounted || !draft) return;

        if (typeof draft.text === "string") {
          setText(draft.text);
        }
        if (typeof draft.isAuto === "boolean") {
          setIsAuto(draft.isAuto);
        }
        if (Array.isArray(draft.attachments) && draft.attachments.length > 0) {
          const restoredAttachments = await persistedToAttachments(
            draft.attachments
          );
          if (isMounted) {
            setAttachmentList(restoredAttachments);
          }
        }
      } catch (error) {
        console.error(error);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [draftKey, setAttachmentList]);

  // 保存草稿（节流/防抖）
  useEffect(() => {
    const handler = setTimeout(async () => {
      try {
        const attachments = await attachmentsToPersisted(attachmentList);
        const draft: ChatDraft = {
          text,
          isAuto,
          attachments,
          updatedAt: Date.now(),
        };
        await setDraft(draftKey, draft);
      } catch (error) {
        console.error(error);
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [text, isAuto, attachmentList, draftKey]);

  const handleContinue = useCallback(async () => {
    if (!threadId) {
      return;
    }

    setIsSending(true);

    try {
      await threadStore.sendMessage(threadId, {
        files: [],
        text: "Continue",
      });

      if (thread) {
        threadStore.set("thread", {
          ...thread,
          status: AgentThreadVoStatus.Running,
        });
      }

      logger.event(VidMuseEvent.ThreadContinue, {
        thread_id: threadId,
      });
    } catch (error) {
      console.error("Failed to continue:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to continue. Please try again.";
      showSendMessageToast(errorMessage);
    } finally {
      setIsSending(false);
    }
  }, [threadId, thread, showSendMessageToast]);

  const handleSendMessage = useCallback(async () => {
    if (!text.trim()) {
      showSendMessageToast("Please enter your video idea to get started");
      return;
    }

    setIsSending(true);

    if (threadId) {
      try {
        await threadStore.sendMessage(threadId, {
          files: attachmentList
            .filter((item): item is FileAttachment => item.type === "file")
            .map((item) => item.file),
          text: text.trim(),
        });

        if (thread) {
          threadStore.set("thread", {
            ...thread,
            status: AgentThreadVoStatus.Running,
          });
        }

        // 提交成功后清理草稿
        await deleteDraft(draftKey);

        logger.event(VidMuseEvent.ThreadSendMessage, {
          thread_id: threadId,
          prompt: text.trim(),
          attachment_count: attachmentList.length,
        });
      } catch (e) {
        console.error("Failed to send message:", e);
        const errorMessage =
          e instanceof Error
            ? e.message
            : "Failed to send message. Please try again.";
        showSendMessageToast(errorMessage);
      } finally {
        setIsSending(false);
      }

      setText("");
      setAttachmentList([]);
      clearAttachments();

      // 重新聚焦到输入框
      setTimeout(() => {
        textAreaRef.current?.focus();
      }, 0);
    } else {
      try {
        // 先上传所有附件，获取 URLs
        const urls = await uploadAttachments(attachmentList);

        const res = await threadStore.createThread({
          urls,
          user_input: text.trim(),
          auto_mode: isAuto,
        });

        if (res.id) {
          // 提交成功后清理"新建"草稿
          await deleteDraft(draftKey);
          router.push(`/thread/${res.id}`);

          logger.event(VidMuseEvent.HomePageCreateThread, {
            thread_id: res.id,
            prompt: text.trim(),
            attachment_count: attachmentList.length,
          });
        }
      } catch (e) {
        console.error("Failed to create thread:", e);
        const errorMessage =
          e instanceof Error
            ? e.message
            : "Failed to create thread. Please try again.";
        showSendMessageToast(errorMessage);
        setIsSending(false);
      }
    }

    subscribeToWebPush();
  }, [
    text,
    attachmentList,
    showSendMessageToast,
    threadId,
    thread,
    isAuto,
    router,
    draftKey,
    clearAttachments,
  ]);

  const handleAutoChange = useCallback(
    async (value: boolean) => {
      if (!threadId) {
        setIsAuto(value);
        return;
      }
      setIsAutoLoading(true);
      try {
        const res = await api.threads.updateThreadAutoMode(threadId, {
          autoMode: value,
        });
        setIsAuto(res.data.autoMode ?? false);
      } finally {
        setIsAutoLoading(false);
      }
    },
    [threadId]
  );

  const handleTextAreaKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage]
  );

  const handleStop = useCallback(async () => {
    if (!threadId) {
      return;
    }
    try {
      setIsRequestStopping(true);
      await threadStore.stopRunning();
    } finally {
      setIsRequestStopping(false);
    }
  }, [threadId]);

  return {
    text,
    isAuto,
    handleAutoChange: handleAutoChange,
    handleMessageTextChange: setText,
    handleSendMessage,
    handleContinue,
    attachmentList,
    textAreaRef,
    handleTextAreaKeyDown,
    uploadProps,
    onRemove,
    isSending,
    isAutoLoading,
    isAutoEnabled: false,
    stopTooltipText,
    stoppingTooltipText,
    isRequestStopping,
    handleStop,
  };
}
